import Axios from 'axios'
import axios from 'lib/axios'

export type ReadinessDateField =
  | 'estimated_tour_ready_date'
  | 'expected_move_in_date'
  | 'current_confirmed_tour_ready_date'
  | 'current_confirmed_rent_ready_date'

export const readinessDateLabels: Record<ReadinessDateField, string> = {
  estimated_tour_ready_date: 'Estimated tour-ready date',
  expected_move_in_date: 'Estimated rent-ready date',
  current_confirmed_tour_ready_date: 'Actual tour-ready date',
  current_confirmed_rent_ready_date: 'Actual rent-ready date'
}

export type ReadinessState = 'unconfirmed' | 'ready' | 'not_ready'
export type ReadinessMilestone = 'tour' | 'rent' | 'both'
export type ReadinessSelection = {
  vacancy_period_id: string
  portfolio_home_id: string
  home_id?: string
  vacancy_id: string
  episode_kind: 'initial' | 'rental'
  address_line_1: string
  address_line_2: string | null
  listed_rent?: string | null
  fund_id?: string | null
  fund_name?: string | null
  market_id?: string | null
  market_name?: string | null
  estimated_tour_ready_date?: string | null
  expected_move_in_date?: string | null
  current_confirmed_tour_ready_date?: string | null
  current_confirmed_rent_ready_date?: string | null
  move_out_date?: string | null
  next_move_in_date?: string | null
  ownership_start?: string | null
  state?: 'current' | 'upcoming' | 'historical' | 'canceled' | 'date_conflict' | 'unresolved'
}

export type ReadinessSort =
  | 'status'
  | 'address'
  | 'start_date'
  | 'end_date'
  | 'estimated_tour_ready_date'
  | 'expected_move_in_date'
  | 'current_confirmed_tour_ready_date'
  | 'current_confirmed_rent_ready_date'
export type ReadinessStatusFilter =
  | 'all'
  | 'current_and_upcoming'
  | 'current'
  | 'upcoming'
  | 'historical'
  | 'canceled'
  | 'needs_review'
export type ReadinessFilterOptions = {
  funds: Array<{ id: string; name: string }>
  markets: Array<{ id: string; name: string }>
}

export type ReadinessDetail = ReadinessSelection & {
  version: number
  live_availability?: {
    required: boolean
    can_sync: boolean
    token?: string
    estimated_tour_ready_date?: string | null
    expected_move_in_date?: string | null
    is_active?: boolean
    warning?: string | null
  }
  estimated_tour_ready_date: string | null
  expected_move_in_date: string | null
  readiness_state: ReadinessState
  current_confirmed_tour_ready_date: string | null
  rent_readiness_state: ReadinessState
  current_confirmed_rent_ready_date: string | null
  today: string
  timezone: string
  can_report_loss?: boolean
  editable: boolean
  reconciliation_message: string | null
  qc_warning: string | null
  estimate_warning?: string | null
  confirmation_history_ids: { tour: string | null; rent: string | null }
  start_date: string | null
  end_date: string | null
  activation: 'record_only'
}
export type ReadinessHistory = {
  id: string
  change_type: string
  action_id: string
  before_values: Record<string, unknown> | null
  after_values: Record<string, unknown>
  affected_fields: string[]
  effective_date: string | null
  recorded_at: string
  actor_name: string | null
  reason: string | null
  corrects_history_id: string | null
  corrects_history_ids?: { tour: string | null; rent: string | null } | null
}
export type ReadinessPage<T> = {
  items: T[]
  total_count: number
  page: number
  per_page: number
}
export type ReadinessAction = {
  action: 'edit_estimates' | 'confirm' | 'correct_confirmation' | 'clear_readiness'
  clear_reason?: 'mistake' | 'no_longer_ready'
  expected_version: number
  expected_legacy_token?: string
  client_key: string
  milestone?: ReadinessMilestone
  estimated_tour_ready_date?: string | null
  expected_move_in_date?: string | null
  effective_date?: string
  rent_effective_date?: string
  reason?: string
  tour_attested?: boolean
  rent_attested?: boolean
  qc_acknowledged?: boolean
  corrects_history_id?: string
  corrects_rent_history_id?: string
}

const readinessPath = (id: string) => `/admin/vacancies/${encodeURIComponent(id)}/readiness`
export const vacancyReadinessApi = {
  async list(
    params: {
      q?: string
      portfolio_home_id?: string
      vacancy_id?: string
      page: number
      per_page?: number
      sort_by?: ReadinessSort
      sort_direction?: 'asc' | 'desc'
      status?: ReadinessStatusFilter
      fund_ids?: string[]
      market_ids?: string[]
      episode_kinds?: string[]
    },
    signal?: AbortSignal
  ): Promise<
    ReadinessPage<ReadinessSelection> & {
      filters: ReadinessFilterOptions
      can_view_rent_prices?: boolean
    }
  > {
    const cancellation = Axios.CancelToken.source()
    const abort = () => cancellation.cancel('Vacancy search changed')
    signal?.addEventListener('abort', abort, { once: true })
    if (signal?.aborted) abort()
    try {
      const { data } = await axios.get('/admin/vacancy-readiness', {
        params,
        paramsSerializer: (values) => {
          const encoded = new URLSearchParams()
          Object.entries(values).forEach(([key, value]) => {
            if (value === undefined || value === null) return
            for (const item of Array.isArray(value) ? value : [value]) {
              encoded.append(key, String(item))
            }
          })
          return encoded.toString()
        },
        cancelToken: cancellation.token
      })
      return data
    } finally {
      signal?.removeEventListener('abort', abort)
    }
  },
  async detail(id: string): Promise<ReadinessDetail> {
    const { data } = await axios.get(readinessPath(id))
    return data
  },
  async history(
    id: string,
    page: number,
    field?: ReadinessDateField
  ): Promise<ReadinessPage<ReadinessHistory>> {
    const { data } = await axios.get(`${readinessPath(id)}/history`, {
      params: { page, per_page: field ? 5 : 20, ...(field ? { field } : {}) }
    })
    return data
  },
  async act(id: string, payload: ReadinessAction): Promise<ReadinessDetail> {
    const { data } = await axios.post(`${readinessPath(id)}/actions`, payload)
    return data
  }
}

export const readinessErrorMessage = (error: unknown): string => {
  const e = error as { message?: string; data?: { message?: string } }
  return e?.message || e?.data?.message || 'Could not reach the server. Please try again.'
}

export const readinessDate = (value: string | null): string =>
  value
    ? new Date(`${value}T12:00:00Z`).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
      })
    : 'Not entered'

export const listedRentLabel = (value?: string | null) => {
  if (value == null || value === '') return 'Not set'
  const amount = Number(value)
  return Number.isFinite(amount) && amount >= 0
    ? amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : 'Invalid price'
}
